import { TooltipComponentPositionCallback } from 'echarts';
import { ARDOQ_DEFAULT_FONT_FAMILY } from '@ardoq/typography';

export const getTooltipPositionFn =
  (containerRef: HTMLDivElement | null): TooltipComponentPositionCallback =>
  (canvasMousePos, _params, tooltipDom, _rect, sizes) => {
    const dashboardContainerDimensions = containerRef
      ? containerRef.getBoundingClientRect()
      : document.body.getBoundingClientRect();

    const margin = 15; // How far away from the mouse should the tooltip be
    const overflowMargin = 5; // If no satisfactory position can be found, how far away from the edge of the window should the tooltip be kept

    // type fix
    const tooltip: HTMLDivElement = tooltipDom as HTMLDivElement;
    // The chart canvas position
    const canvasRect = tooltip
      .parentElement!.getElementsByTagName('canvas')[0]
      .getBoundingClientRect();

    // The mouse coordinates relative to the whole window
    // The first parameter to the position function is the mouse position relative to the canvas
    const mouseX = canvasMousePos[0] + canvasRect.x;
    const mouseY = canvasMousePos[1] + canvasRect.y;

    // The width and height of the tooltip dom element
    const [tooltipWidth, tooltipHeight] = sizes.contentSize;

    // Start by placing the tooltip bottom and right relative to the mouse position
    let xPos = mouseX + margin;
    let yPos = mouseY + margin;

    // The tooltip is overflowing past the right edge of the window
    if (
      xPos + tooltipWidth + overflowMargin >=
      dashboardContainerDimensions.width
    ) {
      // Attempt to place the tooltip to the left of the mouse position
      xPos = mouseX - margin - tooltipWidth;

      // The tooltip is overflowing past the left edge of the window
      if (xPos <= 0) {
        // considering the tooltip placement (bottom right) in case if tooltip cannot be flipped to another side it will be fixed near right edge
        // Place the tooltip a fixed distance from the right edge of the window
        xPos = dashboardContainerDimensions.width - tooltipWidth - margin;
      }
    }

    // The tooltip is overflowing past the bottom edge of the window
    if (
      yPos + tooltipHeight + overflowMargin >=
      dashboardContainerDimensions.height
    ) {
      // Place the tooltip a fixed distance from the bottom edge of the window
      yPos = mouseY - margin - tooltipHeight;

      // The tooltip is overflowing past the top edge of the window
      if (yPos <= 0) {
        // Attempt to place the tooltip to the bottom of the mouse position
        yPos = overflowMargin - yPos + 50;
      }
    }

    // Return the position (converted back to a relative position on the canvas)
    return [xPos - canvasRect.x, yPos - canvasRect.y];
  };

export const DEFAULT_CHART_PRIMARY_COLOR = 'rgb(41, 116, 196)';

const commonChartConfig = {
  textStyle: {
    fontSize: 12,
    fontFamily: ARDOQ_DEFAULT_FONT_FAMILY,
  },
  color: [
    DEFAULT_CHART_PRIMARY_COLOR,
    'rgb(252, 178, 38)',
    'rgb(119, 0, 121)',
    'rgb(58, 161, 134)',
    'rgb(94, 150, 210)',
  ],
  aria: {
    enabled: true,
  },
};

export const groupOtherData = <T>(
  dataArray: T[],
  numberOfSegments: number
): [T[], T[], (index: number) => boolean] => {
  if (dataArray.length <= numberOfSegments) {
    return [[...dataArray], [], () => false];
  }
  const numberOfSegmentsNotIncludingOther = numberOfSegments - 1;
  return [
    dataArray.slice(0, numberOfSegmentsNotIncludingOther),
    dataArray.slice(numberOfSegmentsNotIncludingOther),
    index => index === numberOfSegmentsNotIncludingOther,
  ];
};

export const MAX_OTHER_BAR_TOOLTIP_ITEMS = 5;
export const LEGEND_SWATCH_SIZE = 16;
export const LEGEND_AVAILABLE_WIDTH = 780;
export const MAX_LEGEND_NAME_LENGTH_BEFORE_OVERFLOW = 14;
export const MAX_LEGEND_WIDTH_DIVIDER = 7;
const LEGEND_PADDING_BOTTOM = 16;

// Echarts uses canvas, which doesn't support css variables directly - see https://github.com/apache/echarts/issues/19743
// => pass in the values of respective css variables manually

const pageIconColor = window
  .getComputedStyle(document.documentElement)
  .getPropertyValue('--icon');
export const pageTextStyleColor = window
  .getComputedStyle(document.documentElement)
  .getPropertyValue('--text-moderate');

export const chartLegendConfig = {
  bottom: LEGEND_PADDING_BOTTOM,
  icon: 'roundRect',
  itemWidth: LEGEND_SWATCH_SIZE,
  itemHeight: LEGEND_SWATCH_SIZE,
  type: 'scroll',
  padding: [5, 32, 5, 32],
  pageButtonItemGap: 6,
  pageIconInactiveColor: 'rgba(111,125,144,0.32)', // grey 50 with 32% opacity
  pageIconColor: pageIconColor,
  pageIconSize: [5.4, 8.44],
  pageFormatter: ({ current, total }: Record<string, number>) =>
    `${current} / ${total}`,
  pageTextStyle: {
    color: pageTextStyleColor,
    fontSize: 12,
  },
  pageIcons: {
    // chevron left and right in path format
    horizontal: [
      'path://M12.4234 5.59141C12.0984 5.26641 11.5734 5.26641 11.2484 5.59141L7.42344 9.41641C7.09844 9.74141 7.09844 10.2664 7.42344 10.5914L11.2484 14.4164C11.5734 14.7414 12.0984 14.7414 12.4234 14.4164C12.7484 14.0914 12.7484 13.5664 12.4234 13.2414L9.1901 9.99974L12.4234 6.76641C12.7401 6.44141 12.7401 5.90807 12.4234 5.59141Z',
      'path://M7.75937 5.59246C7.43438 5.91746 7.43438 6.44246 7.75937 6.76746L10.9927 10.0008L7.75937 13.2341C7.43438 13.5591 7.43438 14.0841 7.75937 14.4091C8.08437 14.7341 8.60937 14.7341 8.93437 14.4091L12.7594 10.5841C13.0844 10.2591 13.0844 9.73412 12.7594 9.40912L8.93437 5.58412C8.61771 5.26746 8.08437 5.26746 7.75937 5.59246Z',
    ],
  },
};

export const chartGridConfig = {
  left: '20px',
  right: '32px',
  top: '32px',
  bottom: '50px',
  containLabel: true,
};

export { commonChartConfig };
